import { exigirDefinicionCoherente, } from '../formularios/definicion.js';
/**
 * Los cuestionarios de hito · las preguntas "extra" del protocolo (E3.25, D-01).
 *
 * `pasos-12meses-protocolo.html` pide en seis de las ocho evaluaciones, además de
 * las escalas, datos que el sistema no preguntaba: la medicación con su dosis,
 * las reinternaciones y las reintervenciones, la autonomía y la reintegración.
 * Se perdieron en el pasaje al mapa de datos (`dev/auditoria-datos.md` D-01) y
 * el candado del protocolo (`programa/catalogo-de-datos.ts`) los tenía como
 * pendientes. Acá están, **uno por hito**, como datos del motor: la misma
 * definición arma la pantalla del paciente, valida la respuesta en la API y
 * genera el `Questionnaire` del store.
 *
 * Cada pregunta cita la línea del protocolo de la que sale. **Donde el protocolo
 * da la pregunta entera** (D+240) va textual, con sus opciones. **Donde da un
 * rótulo** ("Medicación actual con dosis", "¿Reinternación en últimos 30
 * días?") la pregunta lo dice en segunda persona, con la ventana del período en
 * el texto, y la redacción va como PROPUESTA en `dev/pendientes-fundacion.md`.
 *
 * Tres reglas que atraviesan los seis:
 *
 *   1. **Nada de texto libre.** La droga sale del catálogo de Fundación (la que
 *      no está se responde "No está en la lista", que es un código); el lugar de
 *      una reinternación es esta institución u otra; la cirugía de una
 *      reintervención es una del catálogo del RFC, el mismo del alta.
 *   2. **No puntúan y no mueven el semáforo** (R-08). Son hechos que el equipo
 *      lee: la reinternación y la reintervención quedan además como `Encounter`
 *      y `Procedure`, y cada medicamento como `MedicationStatement`.
 *   3. **Un "sí" abre las filas y un "no" las apaga.** La pregunta previa ("¿Tomás
 *      alguna medicación?", "¿Tuviste alguna reinternación?") es la que registra
 *      el "no": un grupo vacío no dice nada, y un grupo apagado no se pregunta.
 */
const PROTOCOLO = 'pasos-12meses-protocolo.html';
// ─────────────────────────────────────────────────────────────────────────────
// Las piezas que se repiten
// ─────────────────────────────────────────────────────────────────────────────
/** "No está en la lista": la salida del catálogo, sin texto. */
export const MEDICAMENTO_FUERA_DE_LISTA = {
    codigo: 'no-esta-en-la-lista',
    etiqueta: 'No está en la lista',
};
/** Los `id` de las preguntas que los hechos clínicos leen. Estables para siempre. */
export const PREGUNTAS_DE_HECHOS = {
    tomaMedicacion: 'toma-medicacion',
    medicacion: 'medicacion',
    huboReinternacion: 'hubo-reinternacion',
    reinternaciones: 'reinternaciones',
    huboReintervencion: 'hubo-reintervencion',
    reintervenciones: 'reintervenciones',
};
/** El campo de la droga: una entrada del catálogo de Fundación, o "no está en la lista". */
export const CAMPO_DE_LA_DROGA = {
    tipo: 'catalogo',
    catalogo: 'medicamentos',
    fueraDeLista: MEDICAMENTO_FUERA_DE_LISTA,
};
/** Las preguntas de una fila de medicación: droga, potencia e intervalo. */
export const FILA_DE_MEDICACION = [
    { id: 'droga', texto: '¿Qué medicamento?', campo: CAMPO_DE_LA_DROGA },
    {
        id: 'potencia',
        texto: '¿De cuántos miligramos es cada toma?',
        // Definición de Fundación del 26/09: potencia en miligramos, numérica y
        // abierta. El rango es el del PR 1 (D-255): 0 a 5000 mg, hasta 2 decimales.
        campo: {
            tipo: 'numero',
            unidad: { ucum: 'mg', etiqueta: 'mg' },
            decimales: 2,
            minimo: 0,
            maximo: 5000,
        },
    },
    {
        id: 'intervalo',
        texto: '¿Cada cuántas horas la tomás?',
        // Intervalo en horas, entero: de 1 a 168 (una vez por semana).
        campo: {
            tipo: 'numero',
            unidad: { ucum: 'h', etiqueta: 'horas' },
            decimales: 0,
            minimo: 1,
            maximo: 168,
        },
    },
];
/**
 * Dónde fue la reinternación. El protocolo pide "dónde"; no hay un catálogo de
 * instituciones y el paciente no lo escribe. Las dos opciones son las que el
 * registro clínico puede decir sin inventar: la institución que lo sigue (su
 * `Organization` está en el store y va como `serviceProvider`) u otra (D-259).
 */
export const LUGARES_DE_REINTERNACION = {
    tipo: 'opcion',
    opciones: [
        { codigo: 'esta-institucion', etiqueta: 'En esta misma institución' },
        { codigo: 'otra-institucion', etiqueta: 'En otra institución' },
    ],
};
/**
 * Las cirugías del catálogo del RFC (campo 5.09, ADR-009), las mismas del alta.
 *
 * El catálogo vive en `@pasos/fhir` con sus anclas SNOMED, y este paquete no
 * depende de él: acá van los códigos y las etiquetas, y un test de
 * `@pasos/fhir` falla si esta lista y la del RFC dejan de decir lo mismo.
 */
export const CIRUGIAS_DEL_RFC = {
    tipo: 'opcion',
    opciones: [
        { codigo: 'clavo-intramedular', etiqueta: 'Clavo intramedular' },
        { codigo: 'hemi-monoblock-cementada', etiqueta: 'Hemiartroplastia monoblock cementada' },
        { codigo: 'hemi-bipolar-cementada', etiqueta: 'Hemiartroplastia bipolar cementada' },
        { codigo: 'hemi-no-cementada', etiqueta: 'Hemiartroplastia no cementada' },
        { codigo: 'ptc-cementada', etiqueta: 'Prótesis total de cadera cementada' },
        { codigo: 'ptc-no-cementada', etiqueta: 'Prótesis total de cadera no cementada' },
        { codigo: 'ptc-hibrida', etiqueta: 'Prótesis total de cadera híbrida' },
        { codigo: 'tornillo-deslizante', etiqueta: 'Tornillo deslizante de cadera' },
        { codigo: 'tornillos-canulados', etiqueta: 'Tornillos canulados' },
        { codigo: 'placa-bloqueada', etiqueta: 'Placa bloqueada de cadera' },
        { codigo: 'girdlestone', etiqueta: 'Amputación transcervical (Girdlestone)' },
        { codigo: 'se-desconoce', etiqueta: 'Se desconoce' },
    ],
};
/** Una fecha de lo que pasó durante el programa: no futura, no antes del egreso. */
const FECHA_DEL_PROGRAMA = { tipo: 'fecha', noFutura: true, desdeElDiaCero: true };
function siNo(etiquetaNo = 'No') {
    return { tipo: 'binario', etiquetaSi: 'Sí', etiquetaNo, puntosSi: 0, puntosNo: 0 };
}
function cuandoDijoQueSi(pregunta) {
    return [{ pregunta, operador: 'igual', valor: true }];
}
/**
 * La medicación actual: "¿Tomás alguna medicación?" y, si sí, las filas.
 *
 * "No tomo medicación" es la pregunta previa que apaga el grupo (prompt del PR 2).
 * `rotulo` es lo que dice el protocolo en ese hito, con su línea.
 */
function medicacion(rotulo) {
    return [
        {
            id: PREGUNTAS_DE_HECHOS.tomaMedicacion,
            texto: '¿Tomás alguna medicación actualmente?',
            campo: siNo('No tomo medicación'),
            presentacion: { modo: 'lista' },
            ayuda: rotulo,
            opcional: false,
        },
        {
            id: PREGUNTAS_DE_HECHOS.medicacion,
            texto: '¿Qué medicación tomás?',
            campo: {
                tipo: 'grupo',
                minimo: 1,
                maximo: 15,
                etiquetaDeFila: 'Medicamento',
                preguntas: [...FILA_DE_MEDICACION],
            },
            presentacion: { modo: 'propia' },
            ayuda: 'Elegí cada medicamento de la lista y contanos de cuántos miligramos es cada toma y ' +
                'cada cuántas horas la tomás. Si no está en la lista, elegí "No está en la lista".',
            cuando: cuandoDijoQueSi(PREGUNTAS_DE_HECHOS.tomaMedicacion),
            opcional: false,
        },
    ];
}
/** Las filas de las reinternaciones: la fecha y dónde. */
const FILAS_DE_REINTERNACION = [
    { id: 'fecha', texto: '¿Qué día te internaron?', campo: FECHA_DEL_PROGRAMA },
    { id: 'lugar', texto: '¿Dónde?', campo: LUGARES_DE_REINTERNACION },
];
/** Las filas de las reintervenciones: la fecha y la cirugía del RFC. */
const FILAS_DE_REINTERVENCION = [
    { id: 'fecha', texto: '¿Qué día te operaron?', campo: FECHA_DEL_PROGRAMA },
    { id: 'cirugia', texto: '¿Qué cirugía te hicieron?', campo: CIRUGIAS_DEL_RFC },
];
/** El grupo de reinternaciones, que aparece cuando `siDijoQueSi` es verdadero. */
function grupoDeReinternaciones(siDijoQueSi) {
    return {
        id: PREGUNTAS_DE_HECHOS.reinternaciones,
        texto: 'Contanos cada reinternación',
        campo: {
            tipo: 'grupo',
            minimo: 1,
            maximo: 10,
            etiquetaDeFila: 'Reinternación',
            preguntas: FILAS_DE_REINTERNACION,
        },
        presentacion: { modo: 'propia' },
        cuando: cuandoDijoQueSi(siDijoQueSi),
        opcional: false,
    };
}
/** El grupo de reintervenciones, que aparece cuando `siDijoQueSi` es verdadero. */
function grupoDeReintervenciones(siDijoQueSi) {
    return {
        id: PREGUNTAS_DE_HECHOS.reintervenciones,
        texto: 'Contanos cada cirugía',
        campo: {
            tipo: 'grupo',
            minimo: 1,
            maximo: 10,
            etiquetaDeFila: 'Cirugía',
            preguntas: FILAS_DE_REINTERVENCION,
        },
        presentacion: { modo: 'propia' },
        cuando: cuandoDijoQueSi(siDijoQueSi),
        opcional: false,
    };
}
/** Una pregunta de sí o no que abre un grupo. */
function laQueAbre(id, texto, ayuda) {
    return { id, texto, campo: siNo(), presentacion: { modo: 'lista' }, ayuda, opcional: false };
}
function reinternaciones(ventana) {
    return [
        laQueAbre(PREGUNTAS_DE_HECHOS.huboReinternacion, ventana.pregunta, ventana.rotulo),
        grupoDeReinternaciones(PREGUNTAS_DE_HECHOS.huboReinternacion),
    ];
}
function reintervenciones(ventana) {
    return [
        laQueAbre(PREGUNTAS_DE_HECHOS.huboReintervencion, ventana.pregunta, ventana.rotulo),
        grupoDeReintervenciones(PREGUNTAS_DE_HECHOS.huboReintervencion),
    ];
}
// ─────────────────────────────────────────────────────────────────────────────
// Autonomía (D+240 y D+300)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Las tres preguntas de autonomía de D+240, **textuales con sus opciones**
 * (`pasos-12meses-protocolo.html` líneas 703, 704 y 705). D+300 pide "nivel de
 * autonomía en desplazamientos, actividades cotidianas y participación social
 * (3 preguntas)" sin enunciarlas (línea 751): se usan las mismas tres, con los
 * nombres de D+300, y va como PROPUESTO (prompt del PR 2).
 */
const COMPRAS_O_TRAMITES = {
    // Línea 703. "solo/a" se escribe "solo o sola", como el P8 del microcheck
    // mensual: la frase entera, sin barra (ADR-016).
    texto: '¿Podés realizar compras o trámites básicos solo o sola?',
    campo: {
        tipo: 'opcion',
        opciones: [
            { codigo: 'si', etiqueta: 'Sí' },
            { codigo: 'con-ayuda', etiqueta: 'Con ayuda' },
            { codigo: 'no', etiqueta: 'No' },
        ],
    },
    presentacion: { modo: 'lista' },
    opcional: false,
};
const TRANSPORTE = {
    // Línea 704.
    texto: '¿Podés usar transporte público o desplazarte en auto (como pasajero o conductor) sin ' +
        'dificultad?',
    campo: {
        tipo: 'opcion',
        opciones: [
            { codigo: 'si', etiqueta: 'Sí' },
            { codigo: 'con-dificultad', etiqueta: 'Con dificultad' },
            { codigo: 'no', etiqueta: 'No' },
        ],
    },
    presentacion: { modo: 'lista' },
    opcional: false,
};
const ACTIVIDADES_GRUPALES = {
    // Línea 705.
    texto: '¿Participás de actividades grupales, religiosas o recreativas fuera del hogar?',
    campo: {
        tipo: 'opcion',
        opciones: [
            { codigo: 'si', etiqueta: 'Sí' },
            { codigo: 'a-veces', etiqueta: 'A veces' },
            { codigo: 'no', etiqueta: 'No' },
        ],
    },
    presentacion: { modo: 'lista' },
    opcional: false,
};
// ─────────────────────────────────────────────────────────────────────────────
// Reintegración (D+360)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Nivel de reintegración por dominio, 0 a 3 (línea 797: "trabajo, actividades
 * sociales, recreativas", de 0 a 3 por dominio). El protocolo no nombra las
 * anclas: las de los extremos van como PROPUESTAS. No puntúa: es un dato por
 * dominio y no un instrumento con total.
 */
function reintegracion(id, dominio) {
    return {
        id,
        texto: `¿Cuánto retomaste ${dominio}?`,
        campo: {
            tipo: 'puntaje',
            minimo: 0,
            maximo: 3,
            etiquetaMinimo: 'Nada',
            etiquetaMaximo: 'Como antes de la fractura',
            puntua: false,
        },
        presentacion: { modo: 'lista' },
        opcional: false,
    };
}
// ─────────────────────────────────────────────────────────────────────────────
// Los seis cuestionarios de hito
// ─────────────────────────────────────────────────────────────────────────────
/** D+28 · el cuestionario ampliado de la evaluación de 30 días (líneas 488 a 490). */
export const CUESTIONARIO_D28 = exigirDefinicionCoherente({
    id: 'cuestionario-d28',
    clase: 'cuestionario',
    titulo: 'Tu primer mes',
    version: '0.1.0',
    estado: 'draft',
    preguntas: [
        // Línea 488: "Medicación actual con dosis".
        ...medicacion('Medicación actual con dosis'),
        // Línea 489: "¿Reinternación en últimos 30 días?".
        ...reinternaciones({
            pregunta: '¿Tuviste alguna reinternación en los últimos 30 días?',
            rotulo: 'Reinternación en los últimos 30 días',
        }),
        // Línea 490: "¿Reintervención quirúrgica?".
        ...reintervenciones({
            pregunta: '¿Tuviste alguna reintervención quirúrgica en los últimos 30 días?',
            rotulo: 'Reintervención quirúrgica',
        }),
    ],
});
/** D+120 · medicación, reinternaciones y reintervenciones en 4 meses (línea 578). */
export const CUESTIONARIO_D120 = exigirDefinicionCoherente({
    id: 'cuestionario-d120',
    clase: 'cuestionario',
    titulo: 'Tus primeros 4 meses',
    version: '0.1.0',
    estado: 'draft',
    preguntas: [
        ...medicacion('Medicación actual'),
        ...reinternaciones({
            pregunta: '¿Tuviste alguna reinternación en estos 4 meses desde el alta?',
            rotulo: 'Reinternaciones en 4 meses',
        }),
        ...reintervenciones({
            pregunta: '¿Tuviste alguna reintervención quirúrgica en estos 4 meses desde el alta?',
            rotulo: 'Reintervenciones en 4 meses',
        }),
    ],
});
/** D+180 · medicación, reinternaciones y reintervenciones en 6 meses (línea 623). */
export const CUESTIONARIO_D180 = exigirDefinicionCoherente({
    id: 'cuestionario-d180',
    clase: 'cuestionario',
    titulo: 'Tus primeros 6 meses',
    version: '0.1.0',
    estado: 'draft',
    preguntas: [
        ...medicacion('Medicación actual'),
        ...reinternaciones({
            pregunta: '¿Tuviste alguna reinternación en estos 6 meses desde el alta?',
            rotulo: 'Reinternaciones en 6 meses',
        }),
        ...reintervenciones({
            pregunta: '¿Tuviste alguna reintervención quirúrgica en estos 6 meses desde el alta?',
            rotulo: 'Reintervenciones en 6 meses',
        }),
    ],
});
/** D+240 · las tres preguntas de autonomía, textuales (líneas 703 a 705). */
export const CUESTIONARIO_D240 = exigirDefinicionCoherente({
    id: 'cuestionario-d240',
    clase: 'cuestionario',
    titulo: 'Tu autonomía',
    version: '0.1.0',
    estado: 'draft',
    preguntas: [
        { id: 'compras-o-tramites', ...COMPRAS_O_TRAMITES },
        { id: 'transporte', ...TRANSPORTE },
        { id: 'actividades-grupales', ...ACTIVIDADES_GRUPALES },
    ],
});
/**
 * D+300 · actualización de medicación (línea 749), reinternaciones o
 * reintervenciones en los últimos 4 meses (línea 750) y las tres de autonomía
 * (línea 751), con los nombres de D+300 y el texto de D+240.
 */
export const CUESTIONARIO_D300 = exigirDefinicionCoherente({
    id: 'cuestionario-d300',
    clase: 'cuestionario',
    titulo: 'Tus últimos 4 meses',
    version: '0.1.0',
    estado: 'draft',
    preguntas: [
        ...medicacion('Actualización de medicación'),
        ...reinternaciones({
            pregunta: '¿Tuviste alguna reinternación en los últimos 4 meses?',
            rotulo: 'Reinternaciones en los últimos 4 meses',
        }),
        ...reintervenciones({
            pregunta: '¿Tuviste alguna reintervención quirúrgica en los últimos 4 meses?',
            rotulo: 'Reintervenciones en los últimos 4 meses',
        }),
        // Desplazamientos: la de transporte. Actividades cotidianas: la de compras o
        // trámites. Participación social: la de actividades grupales.
        { id: 'desplazamientos', ...TRANSPORTE },
        { id: 'actividades-cotidianas', ...COMPRAS_O_TRAMITES },
        { id: 'participacion-social', ...ACTIVIDADES_GRUPALES },
    ],
});
/**
 * La pregunta de D+360 que confirma el total del año. Se exporta porque la
 * pantalla del paciente muestra, antes de ella, lo que ya contó en el año.
 */
export const PREGUNTA_DE_OTRA_REINTERNACION = 'otra-reinternacion';
export const PREGUNTA_DE_OTRA_REINTERVENCION = 'otra-reintervencion';
/**
 * D+360 · el cierre anual (líneas 795 a 797).
 *
 *   - **Medicación actual** (795): la misma pregunta; la comparación contra el
 *     egreso (o contra D+28, diciéndolo) la ve el equipo, no se le pregunta al
 *     paciente.
 *   - **Total de reinternaciones y reintervenciones en 12 meses** (796): no se
 *     vuelve a preguntar. Se **deriva** de lo que contestó en el año, se le
 *     muestra, y se le pregunta si falta alguna. Es también lo que cubre los
 *     sesenta días entre D+300 y D+360, que ningún otro cuestionario pregunta.
 *   - **Reintegración** (797): trabajo, actividades sociales y recreativas, 0 a
 *     3 por dominio.
 *
 * La adherencia total (798) es derivada y ya existe (E3.23): no se pregunta.
 */
export const CUESTIONARIO_D360 = exigirDefinicionCoherente({
    id: 'cuestionario-d360',
    clase: 'cuestionario',
    titulo: 'Tu año',
    version: '0.1.0',
    estado: 'draft',
    preguntas: [
        ...medicacion('Medicación actual'),
        laQueAbre(PREGUNTA_DE_OTRA_REINTERNACION, '¿Tuviste alguna otra reinternación en el año que no esté en esta lista?', 'Total de reinternaciones y reintervenciones en 12 meses'),
        grupoDeReinternaciones(PREGUNTA_DE_OTRA_REINTERNACION),
        laQueAbre(PREGUNTA_DE_OTRA_REINTERVENCION, '¿Tuviste alguna otra reintervención quirúrgica en el año que no esté en esta lista?', 'Total de reinternaciones y reintervenciones en 12 meses'),
        grupoDeReintervenciones(PREGUNTA_DE_OTRA_REINTERVENCION),
        reintegracion('reintegracion-trabajo', 'tu trabajo'),
        reintegracion('reintegracion-social', 'tus actividades sociales'),
        reintegracion('reintegracion-recreativa', 'tus actividades recreativas'),
    ],
});
/**
 * La medicación al egreso · bloque opcional del paso 2 del alta (decisión de
 * Cowork, informada a Fundación). Es la base de la comparación de D+360. Sólo
 * el grupo: el alta la carga el equipo, y "no toma medicación" es no cargar el
 * bloque.
 */
export const MEDICACION_AL_EGRESO = exigirDefinicionCoherente({
    id: 'medicacion-al-egreso',
    clase: 'cuestionario',
    titulo: 'Medicación al egreso',
    version: '0.1.0',
    estado: 'draft',
    preguntas: [
        {
            id: PREGUNTAS_DE_HECHOS.medicacion,
            texto: 'Medicación al egreso',
            campo: {
                tipo: 'grupo',
                minimo: 1,
                maximo: 15,
                etiquetaDeFila: 'Medicamento',
                preguntas: [...FILA_DE_MEDICACION],
            },
            presentacion: { modo: 'propia' },
            opcional: false,
        },
    ],
});
/** Los seis cuestionarios de hito, en el orden del protocolo. */
export const CUESTIONARIOS_DE_HITO = [
    CUESTIONARIO_D28,
    CUESTIONARIO_D120,
    CUESTIONARIO_D180,
    CUESTIONARIO_D240,
    CUESTIONARIO_D300,
    CUESTIONARIO_D360,
];
/** Las líneas del protocolo de las que sale cada cuestionario. */
export const FUENTE_DE_CADA_CUESTIONARIO = {
    'cuestionario-d28': [488, 489, 490],
    'cuestionario-d120': [578],
    'cuestionario-d180': [623],
    'cuestionario-d240': [703, 704, 705],
    'cuestionario-d300': [749, 750, 751],
    'cuestionario-d360': [795, 796, 797],
};
export const ARCHIVO_DEL_PROTOCOLO = PROTOCOLO;
//# sourceMappingURL=hitos.js.map